import { Component, OnInit, OnDestroy, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ThemeService } from '@app/core/services/theme/theme.service';
import { Subscription } from 'rxjs';

export enum FestivalType {
  NONE = 'none',
  CHRISTMAS = 'christmas',
  NEW_YEAR = 'new_year',
  EASTER = 'easter',
  HALLOWEEN = 'halloween',
  DIWALI = 'diwali',
  THANKSGIVING = 'thanksgiving',
  AUTUMN = 'autumn',
  REMEMBRANCE = 'remembrance'
}

const MAPLE_LEAVES = ['maple1.png', 'maple2.png', 'maple3.png'] as const;

export interface FallingMaple {
  src: string;
  left: string;
  size: string;
  duration: string;
  delay: string;
  drift: string;
  spin: string;
}

/**
 * Festive login / layout date windows (local calendar, 2026 schedule).
 * getMonth(): 0=Jan … 8=Sep, 9=Oct, 10=Nov, 11=Dec.
 */
/** Start of Fall: September 23–29 */
export function isFallOfSummerEvent(date = new Date()): boolean {
  const monthIndex = date.getMonth();
  const day = date.getDate();
  return monthIndex === 8 && day >= 23 && day <= 29;
}

/** Thanksgiving (Canada): October 6–12 */
export function isThanksgivingEvent(date = new Date()): boolean {
  const monthIndex = date.getMonth();
  const day = date.getDate();
  return monthIndex === 9 && day >= 6 && day <= 12;
}

/** Halloween: October 26 – November 1 */
export function isHalloweenEvent(date = new Date()): boolean {
  const monthIndex = date.getMonth();
  const day = date.getDate();
  return (monthIndex === 9 && day >= 26) || (monthIndex === 10 && day === 1);
}

/** Remembrance Day (Canada): November 8–11 */
export function isRemembranceEvent(date = new Date()): boolean {
  const monthIndex = date.getMonth();
  const day = date.getDate();
  return monthIndex === 10 && day >= 5 && day <= 11;
}

/**
 * Resolves the currently active festival for the given date.
 * Exclusive windows — only one applies (no overlap in the 2026 schedule).
 * Order: 1) Thanksgiving  2) Halloween  3) Remembrance  4) Start of Fall
 */
export function getActiveFestivalType(date = new Date()): FestivalType {
  if (isThanksgivingEvent(date)) {
    return FestivalType.THANKSGIVING;
  }
  if (isHalloweenEvent(date)) {
    return FestivalType.HALLOWEEN;
  }
  if (isRemembranceEvent(date)) {
    return FestivalType.REMEMBRANCE;
  }
  if (isFallOfSummerEvent(date)) {
    return FestivalType.AUTUMN;
  }

  switch (date.getMonth() + 1) {
    case 12:
      return FestivalType.CHRISTMAS;
    case 1:
      return FestivalType.NEW_YEAR;
    case 4:
      return FestivalType.EASTER;
    default:
      return FestivalType.NONE;
  }
}

export function isFestivalActive(date = new Date()): boolean {
  return getActiveFestivalType(date) !== FestivalType.NONE;
}

/**
 * Login left/right illustrations are hidden while a date-window event
 * replaces the login background (autumn, Thanksgiving, Halloween, Remembrance).
 */
export function shouldHideLoginIllustrations(date = new Date()): boolean {
  const type = getActiveFestivalType(date);
  return (
    type === FestivalType.AUTUMN ||
    type === FestivalType.THANKSGIVING ||
    type === FestivalType.HALLOWEEN ||
    type === FestivalType.REMEMBRANCE
  );
}

function buildFallingMaples(): FallingMaple[] {
  const spots = [4, 12, 20, 28, 37, 46, 55, 64, 73, 82, 90, 96];
  return spots.map((left, index) => ({
    src: `assets/images/events/${MAPLE_LEAVES[index % MAPLE_LEAVES.length]}`,
    left: `${left}%`,
    size: `${44 + (index % 4) * 14}px`,
    duration: `${8 + (index % 5)}s`,
    delay: `${(index % 6) * 0.45}s`,
    drift: `${index % 2 === 0 ? 36 : -28}px`,
    spin: `${index % 2 === 0 ? 280 : -240}deg`,
  }));
}

@Component({
  selector: 'app-festival-decoration',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './festival-decoration.html',
  styleUrl: './festival-decoration.scss'
})
export class FestivalDecoration implements OnInit, OnDestroy {
  readonly fallingMaples = buildFallingMaples();
  currentMonth = signal<number>(new Date().getMonth() + 1); // 1-12
  currentDay = signal<number>(new Date().getDate());
  currentTheme = signal<'light' | 'dark'>('light');
  /** Leaves fall for the duration of the autumn / Thanksgiving login event. */
  readonly leavesActive = signal(false);
  private readonly themeService = inject(ThemeService);
  private themeSubscription?: Subscription;
  private dateCheckId = 0;

  festivalType = computed<FestivalType>(() =>
    getActiveFestivalType(
      new Date(new Date().getFullYear(), this.currentMonth() - 1, this.currentDay())
    )
  );

  isChristmas = computed(() => this.festivalType() === FestivalType.CHRISTMAS);
  isAutumn = computed(() => this.festivalType() === FestivalType.AUTUMN);
  isThanksgiving = computed(() => this.festivalType() === FestivalType.THANKSGIVING);
  showFallingLeaves = computed(
    () => this.leavesActive() && (this.isAutumn() || this.isThanksgiving())
  );
  /**
   * Only mount the fixed overlay when particles actually render.
   * An empty full-screen layer (e.g. Thanksgiving on main layout) sits above
   * page text and hides the browser Ctrl+F match highlight.
   */
  showFestival = computed(() => this.isChristmas() || this.showFallingLeaves());
  isLightTheme = computed(() => this.currentTheme() === 'light');

  ngOnInit(): void {
    const theme = this.themeService.getTheme();
    this.currentTheme.set(theme === 'light' ? 'light' : 'dark');

    this.themeSubscription = this.themeService.getCurrentTheme().subscribe(theme => {
      this.currentTheme.set(theme === 'light' ? 'light' : 'dark');
    });

    this.updateDate();
    this.dateCheckId = window.setInterval(() => this.updateDate(), 60000);
    this.startMapleFall();
  }

  ngOnDestroy(): void {
    this.themeSubscription?.unsubscribe();
    window.clearInterval(this.dateCheckId);
  }

  private updateDate(): void {
    const now = new Date();
    this.currentMonth.set(now.getMonth() + 1);
    this.currentDay.set(now.getDate());
    // Re-evaluate if the festive window opened/closed while the page stayed open
    this.startMapleFall();
    if (!this.shouldShowFallingLeaves()) {
      this.leavesActive.set(false);
    }
  }

  private shouldShowFallingLeaves(): boolean {
    if (!this.isLoginAuthPage()) {
      return false;
    }
    const type = getActiveFestivalType();
    return type === FestivalType.AUTUMN || type === FestivalType.THANKSGIVING;
  }

  /** Continuous fall only on login / OTP login during the festive window. */
  private startMapleFall(): void {
    if (this.leavesActive()) return;
    if (!this.shouldShowFallingLeaves()) {
      return;
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    this.leavesActive.set(true);
  }

  /** Login and OTP login only — not the main app shell after sign-in. */
  private isLoginAuthPage(): boolean {
    const path = `${window.location.pathname}${window.location.hash}`.toLowerCase();
    if (path.includes('forgot')) {
      return false;
    }
    return (
      path.includes('login') ||
      path.includes('login-with-otp') ||
      /#\/?$/.test(path)
    );
  }
}
